// SurescriptsRecordLocator.ts
import { DatabaseIdTypes } from "../../Enums/ChoiceEnums";
import { DisplayStyle } from "../../Enums/UiEnums";
import { ApiResponseSimple, combinePaths, deleteObjectWithApiError, getApiErrorText, getFromUrl, getObject, getText, IStatusResponse, postObject, postVoidResult, putObject } from "../ChoiceApi";
import { NoTzDate } from "../DateUtils";
import { DatabaseId } from "../Models/DatabaseIdModel";
import { showToast } from "../Notification";
import { EDocumentCommunicationUserType, IReviewInfo } from "./EDocument";
import { ChoiceApi } from "../../LibMain";
import { PaginatedResult } from "./Pagination";
import { IDocumentListItem } from "../../ChoiceScreens/Surescripts/Models/IDocumentListItem";
import { DocumentRetrieveStatus } from "../../ChoiceScreens/Surescripts/Models/DocumentRetrieveStatus";

export async function loadPatientIntoRLE(patientId: number): Promise<boolean|undefined>
{
    return await putObject(`api/Surescripts/MasterPatientIndex/${patientId}`);
}

export async function findPatient(patientId: number): Promise<IFindPatientResp | undefined> {
    return await getObject(`api/Surescripts/MasterPatientIndex/${patientId}`);
}

export interface IFindPatientResp
{
    surescriptsPatientId?: string,
    assigningAuthorityId?: string,
    blockingStartTimestampUTC?: Date,
    blockingExpectedEndTimestampUTC?: Date
}


export async function getDocumentList(patientId: number, surescriptsPatientId: string, assigningAuthorityId?: string): Promise<IGetDocumentListResp | undefined>
{
    if (undefined === assigningAuthorityId)
    {
        return await getObject(`api/Surescripts/DocumentList/${patientId}/${surescriptsPatientId}`);
    }

    return await getObject(`api/Surescripts/DocumentList/${patientId}/${surescriptsPatientId}/${assigningAuthorityId}`);
}

export async function getDocumentBase64(docUrlSuffix: string): Promise<string|undefined>
{
    return await getText(`api/Surescripts/Doc/Base64/${docUrlSuffix}`);
}

// This checks to see if there are cached documents for the specified patient
export async function getCachedDocuments(patientId: number): Promise<ICachedDocuments | undefined>
{
    return getObject(`api/Surescripts/MasterPatientIndex/${patientId}/CachedDocuments`);
}

// This checks to see if there are cached documents for the specified patient
export async function deleteCachedDocuments(patientId: number): Promise<boolean>
{
    const resp = await deleteObjectWithApiError(`api/Surescripts/MasterPatientIndex/${patientId}/CachedDocuments`);

    // Display a toast where there is an erro
    if (resp.error)
    {
        showToast(`Error deleting cached documents: ${getApiErrorText(resp.error)}`, DisplayStyle.Danger);
        return false;
    }
    return true;
}

// Import a CCD from Surescripts into OMD
export async function importCCD(documentUrl: string, patientId: number): Promise<ApiResponseSimple<DatabaseId>|undefined> {
    const route = combinePaths([`api/Surescripts/CCD/${patientId}`, documentUrl], false);
    return await putObject(route);
}

// Import a file into EDocuments from Surescripts into OMD
export async function importEDocument(documentUrl: string, patientId: number, inputs: ImportEDocumentInput): Promise<ApiResponseSimple<DatabaseId>|undefined> {
    const route = combinePaths([`api/Surescripts/EDocument/${patientId}`, documentUrl], false);    
    return await postObject(route, inputs);
}

export async function getPatientDocumentStatusInfo(patientId: number) : Promise<IDocumentStatusInfo[]|undefined>
{
    return await getObject(`api/Surescripts/DocumentStatus/${patientId}`);    
}

// GEt a URL pointing to an API endpoint that will return the specified Surescripts document
export function getDocumentEndpointUrl(documentUrl: string, patientId: number): string
{
    const route = combinePaths([`api/Surescripts/Doc/${patientId}`, documentUrl], false);
    return ChoiceApi.fullUrl(route);
}

export function getDocumentAsResource(documentUrl: string, patientId: number): Promise<Response>
{
    const route = combinePaths([`api/Surescripts/Doc/${patientId}`, documentUrl], false);
    return getFromUrl(ChoiceApi.fullUrl(route));
}

export function getSharingHistory(patientId: number, cursor?:number, pageSize: number=1000): Promise<PaginatedResult<ISurescriptsDocumentShareEvent>|undefined>
{
    return getObject(`api/Surescripts/Patient/${patientId}/SharingHistory?pageSize=${pageSize ?? ""}&pageCursor=${cursor ?? ""}`);
}

export function getRecordLocatorOrganizationInfo(): Promise<IRecordLocatorOrganizationAndSurescriptsInfo|undefined>
{
    return getObject("api/Surescripts/RLE/Organization");
}

export async function saveRecordLocatorOrganizationInfo(info: IRecordLocatorOrganizationInfo): Promise<IStatusResponse>
{
    return await postVoidResult("api/Surescripts/RLE/Organization", info);
}

export interface IGetDocumentListResp
{
    documents?: IDocumentListItem[],
	blockingStartTimestampUTC?: Date,
	blockingExpectedEndTimestampUTC?: Date,
}

export interface ICachedDocuments
{
    documents?: IDocumentListItem[],
    documentListFetchedOnDateTimeUTC?: Date
}

export interface ImportEDocumentInput
{
    documentName: string,
    documentComment?: string,
    folderId?: number,
    receivedDate?: NoTzDate,  // Date string
    documentTypeId?: number,

    communicationUserType?: EDocumentCommunicationUserType,
    communicationUserId?: number,

    reviewInfo?: IReviewInfo    
}

export interface IDocumentStatusInfo
{
    surescriptsDocumentUrl: string,
    documentRetrieveStatus: DocumentRetrieveStatus,
    saveErrorMsg?: string,
    savedToIdType?: DatabaseIdTypes,
    savedToId?: number
}

export interface ISurescriptsDocumentShareEvent {
    surescriptsDocumentShareEventId: number;
    patientId: number;
    postDateUtc: Date;
    sharedItemId: number;
    sharedItemIdType: number;
    originiatorOrgName?: string | null;
    originiatorHomeCommunityId?: string | null;
    originiatorUserName?: string | null;
    originiatorUserId?: string | null;
    errorDuringCall: boolean;
}

export interface IRecordLocatorOrganizationInfo
{
    organizationName: string;
    street: string;
    city: string;
    stateAbbr: string;
    zipCode: string;
    phone: string;
    fax?: string;
}

export interface IRecordLocatorOrganizationAndSurescriptsInfo
{
    
    orgInfo: IRecordLocatorOrganizationInfo, 
    homeCommunityId: string, 
    surescriptsOrgId?: string
}